import { Badge, Box, Checkbox, HStack, Icon, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { CpvNode } from "./cpvHierarchy";

type Props = {
  node: CpvNode;
  isSelected: boolean;
  isExpanded: boolean;
  hasSelectedDescendant: boolean;
  hasChildren: boolean;
  isOnlyAdditional: boolean;
  posInSet: number;
  setSize: number;
  onToggleSelect: (code: string) => void;
  onToggleExpand: (code: string) => void;
};

export const CpvTreeNode = memo(({
  node,
  isSelected,
  isExpanded,
  hasSelectedDescendant,
  hasChildren,
  isOnlyAdditional,
  posInSet,
  setSize,
  onToggleSelect,
  onToggleExpand,
}: Props) => {
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language.startsWith("no");
  const label = isNorwegian ? node.nameNb : node.nameEn;

  return (
    <HStack
      role="treeitem"
      aria-level={node.depth + 1}
      // The DOM is flat, so the row has to state its own place: without these a reader is told the
      // level but never "3 of 12", which is how you know a branch has more in it.
      aria-posinset={posInSet}
      aria-setsize={setSize}
      aria-expanded={hasChildren ? isExpanded : undefined}
      // The APG's checkbox-tree pattern: "mixed" is the partial selection the checkbox below can
      // only show visually. tabIndex is owned by the tree's roving focus, not set here.
      aria-checked={isSelected ? "true" : hasSelectedDescendant ? "mixed" : "false"}
      onClick={() => onToggleSelect(node.code)}
      spacing="2"
      pl={`${node.depth * 1.25}rem`}
      py="1"
      align="center"
      borderRadius="sm"
      cursor="pointer"
      outline="none"
      _hover={{ bg: "smBackgroundSecondary" }}
      _focusVisible={{ boxShadow: "smFocusRing" }}
    >
      <Box w="6" flexShrink="0" display="flex" alignItems="center" justifyContent="center">
        {hasChildren && (
          <Icon
            as={ChevronRightIcon}
            aria-hidden
            boxSize="4"
            color="smMuted"
            onClick={(event) => {
              event.stopPropagation();
              onToggleExpand(node.code);
            }}
            transform={isExpanded ? "rotate(90deg)" : "none"}
            transition="transform 0.2s ease"
          />
        )}
      </Box>
      {/* Decoration: the row carries the state as aria-checked, and a focusable control inside a
          treeitem would be a second tab stop the pattern does not have. */}
      <Checkbox
        aria-hidden
        tabIndex={-1}
        pointerEvents="none"
        colorScheme="teal"
        isChecked={isSelected}
        isIndeterminate={!isSelected && hasSelectedDescendant}
        sx={{
          ".chakra-checkbox__control[data-indeterminate]": {
            background: "transparent",
            borderStyle: "dashed",
            borderColor: "teal.500",
            color: "teal.500",
          },
        }}
      />
      <Text
        fontFamily="mono"
        fontSize="xs"
        color={isSelected ? "smPrimary" : "smMuted"}
        fontWeight="600"
      >
        {node.code}
      </Text>
      <Text fontSize="sm" color={isSelected ? "smPrimary" : "smText"}>
        {label}
      </Text>
      {isOnlyAdditional && (
        <Badge colorScheme="gray" flexShrink="0" textTransform="none" fontWeight="normal">
          {t("additional code only")}
        </Badge>
      )}
    </HStack>
  );
});

CpvTreeNode.displayName = "CpvTreeNode";
